import type { TournamentDivision, TournamentMatch } from "./api";
import { dayRollSlots, divisionShortName, type DayRollRow } from "./dayRoll";
import { escapeHtml } from "./printTemplate";
import { ramaHeader, ramaLabel, shortGroupName } from "./referees";
import { divisionAccentColor } from "./tournamentDivisions";

// «🖨 Hoja de control» (maqueta Hoja-Control, Eduardo 2026-10-16): la hoja
// que llena a mano el árbitro general. Usa los MISMOS datos que «Rol de
// juego del día» (hora → cancha → partido), con el color de cada categoría,
// el árbitro asignado (o una línea para escribirlo), tres casillas de set en
// blanco y la firma del árbitro general. Pura: se prueba sin la página.

type DivisionInfo = Pick<TournamentDivision, "id" | "name"> &
  Partial<Pick<TournamentDivision, "code" | "gender">>;

export interface ControlSheetOptions {
  dateLabel: string;
  venue: string;
  /** «Todas (8M–12M)» o el nombre de la categoría elegida. */
  categoryLabel: string;
  chiefReferee: string;
  showReferee: boolean;
  divisions: DivisionInfo[];
}

const BLANK_LINE = "_______________";

function playerName(match: TournamentMatch, side: 1 | 2): string {
  const participant = side === 1 ? match.player1 : match.player2;
  return participant?.display_name?.trim() || participant?.short_name?.trim() || "Por definir";
}

/** Clave corta de la categoría + grupo: «8M · A». */
export function controlSheetChip(row: DayRollRow, divisions: DivisionInfo[]): string {
  const division = divisions.find((d) => d.id === row.divisionId);
  const name = division ? divisionShortName(division) : row.divisionName;
  const group = shortGroupName(row.match.group_name);
  return [name, group].filter(Boolean).join(" · ");
}

/** Rama de la hoja: la de las categorías que entran (Eduardo 2026-10-16:
 * varía por categoría). */
export function controlSheetRama(rows: DayRollRow[], divisions: DivisionInfo[]): string {
  const byId = new Map(divisions.map((d) => [d.id, d]));
  return ramaHeader(rows.map((row) => (row.divisionId != null ? byId.get(row.divisionId)?.gender ?? null : null)));
}

/** «Todas (8M–12M)» con las claves de la primera y la última categoría. */
export function controlSheetCategoryLabel(rows: DayRollRow[], divisions: DivisionInfo[]): string {
  const ids = [...new Set(rows.map((row) => row.divisionId).filter((id): id is number => id != null))];
  const ordered = divisions.filter((d) => ids.includes(d.id));
  if (ordered.length === 0) return "—";
  if (ordered.length === 1) return ordered[0].name;
  const first = divisionShortName(ordered[0]);
  const last = divisionShortName(ordered[ordered.length - 1]);
  return `Todas (${first}–${last})`;
}

export const CONTROL_SHEET_PRINT_CSS = `
    .control-sheet { color: #10220f; font-size: 12px; }
    .control-sheet__info { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 6px; margin-bottom: 10px; }
    .control-sheet__info div { border: 1px solid #c9d6c0; border-radius: 8px; padding: 6px 8px; }
    .control-sheet__info span { font-size: 9px; font-weight: 800; letter-spacing: 0.08em; color: #55624f; }
    .control-sheet__info strong { display: block; font-size: 12px; }
    .control-sheet table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    .control-sheet th { font-size: 9px; letter-spacing: 0.06em; text-align: left; padding: 4px 6px; border-bottom: 2px solid #6f9a58; color: #3f4a3a; }
    .control-sheet td { padding: 6px; border-bottom: 1px solid #d8e7cf; vertical-align: middle; overflow: hidden; text-overflow: ellipsis; }
    .control-sheet tr.control-sheet__slot td { background: #eef5e8; font-weight: 800; padding: 4px 6px; border-bottom: 1px solid #6f9a58; }
    .control-sheet td.control-sheet__court { font-weight: 800; text-align: center; font-size: 14px; }
    .control-sheet .control-sheet__chip { display: inline-block; padding: 1px 6px; border: 1.5px solid; border-radius: 999px; font-size: 10px; font-weight: 800; white-space: nowrap; }
    .control-sheet .control-sheet__rama { display: block; font-size: 9px; color: #55624f; margin-top: 2px; }
    .control-sheet td.control-sheet__vs { text-align: center; font-weight: 800; color: #55624f; }
    .control-sheet td.control-sheet__blank { color: #9aa89a; }
    .control-sheet .control-sheet__sets { display: flex; gap: 4px; }
    .control-sheet .control-sheet__sets span { display: inline-block; width: 26px; height: 20px; border: 1.5px solid #3f4a3a; border-radius: 4px; }
    .control-sheet__foot { display: flex; justify-content: space-between; gap: 12px; margin-top: 18px; font-size: 12px; }
`;

/** Cuerpo HTML de la hoja (va dentro de la plantilla de impresión). */
export function controlSheetHtml(rows: DayRollRow[], options: ControlSheetOptions): string {
  const ramaMixed = !["Varonil", "Femenil", "Mixta", "—"].includes(controlSheetRama(rows, options.divisions));
  const byId = new Map(options.divisions.map((d) => [d.id, d]));
  const body = dayRollSlots(rows)
    .map((slot) => {
      const head = `<tr class="control-sheet__slot"><td colspan="${options.showReferee ? 8 : 7}">${escapeHtml(
        slot.time ? `${slot.time} hrs` : "Hora por definir",
      )}</td></tr>`;
      const lines = slot.rows
        .map((row) => {
          const color =
            row.divisionId != null ? divisionAccentColor({ id: row.divisionId, name: row.divisionName }) : "#9aa89a";
          const rama = ramaMixed && row.divisionId != null ? ramaLabel(byId.get(row.divisionId)?.gender ?? null) : null;
          const referee = row.match.referee_name?.trim();
          const refereeCell = options.showReferee
            ? `<td class="${referee ? "" : "control-sheet__blank"}">${escapeHtml(referee || BLANK_LINE)}</td>`
            : "";
          return `<tr data-control-match="${row.match.id}">
          <td class="control-sheet__court">${escapeHtml(row.court || "—")}</td>
          <td><span class="control-sheet__chip" style="border-color:${color};color:${color}">${escapeHtml(
            controlSheetChip(row, options.divisions) || "—",
          )}</span>${rama ? `<span class="control-sheet__rama">${escapeHtml(rama)}</span>` : ""}</td>
          <td>${escapeHtml(playerName(row.match, 1))}</td>
          <td class="control-sheet__vs">VS</td>
          <td>${escapeHtml(playerName(row.match, 2))}</td>
          ${refereeCell}
          <td>${escapeHtml(row.time || "—")}</td>
          <td><div class="control-sheet__sets"><span></span><span></span><span></span></div></td>
        </tr>`;
        })
        .join("");
      return head + lines;
    })
    .join("");
  const refereeCol = options.showReferee ? `<col style="width:15%"/>` : "";
  const refereeHead = options.showReferee ? "<th>ÁRBITRO</th>" : "";
  return `
    <div class="control-sheet">
      <div class="control-sheet__info">
        <div><span>FECHA</span><strong>${escapeHtml(options.dateLabel)}</strong></div>
        <div><span>SEDE</span><strong>${escapeHtml(options.venue || "—")}</strong></div>
        <div><span>CATEGORÍA(S)</span><strong>${escapeHtml(options.categoryLabel)}</strong></div>
        <div><span>RAMA</span><strong>${escapeHtml(controlSheetRama(rows, options.divisions))}</strong></div>
        <div><span>ÁRBITRO GENERAL</span><strong>${escapeHtml(options.chiefReferee.trim() || BLANK_LINE)}</strong></div>
      </div>
      <table>
        <colgroup><col style="width:7%"/><col style="width:11%"/><col/><col style="width:4%"/><col/>${refereeCol}<col style="width:7%"/><col style="width:13%"/></colgroup>
        <thead><tr><th>CANCHA</th><th>GRUPO</th><th>JUGADOR 1</th><th></th><th>JUGADOR 2</th>${refereeHead}<th>HORA</th><th>SETS 1·2·3</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
      <div class="control-sheet__foot">
        <span>Firma árbitro general: ______________________</span>
        <span>Carta horizontal</span>
      </div>
    </div>`;
}
